import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Card as CardMaterialUI,
  GridList,
  GridListTile,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  CardActions,
  CardContent,
  Grid,
  Divider,
  IconButton,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Notifications,
  ChangeHistory,
  Wifi,
  Timeline,
  Straighten,
} from '@material-ui/icons';
import BuildIcon from '@material-ui/icons/Build';
import { isArray } from 'lodash';

import {
  DESCARGA_NF,
  SOBRAS_E_FALTAS,
  MODULO_ARQUEACAO_AUTOMATICA,
} from '~/constants/modules';
import { t, can, number } from '~/helpers';
import { IconBarrel } from '~/icons';
import api from '~/services/api';

import {
  WrapperGrid,
  WrapperCharts,
  ChartCard,
  ChartHeader,
  Chart,
  ChartFooter,
  ChartValue,
  HighlightValue,
} from './styles';

const useStyles = makeStyles(() => ({
  '@keyframes blink': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.5 },
    '100%': { opacity: 0 },
  },
  estoque: {
    width: '100%',
    paddingTop: 0,
    backgroundColor: '#fff',
  },
  estoqueItem: {
    paddingTop: 1,
    paddingBottom: 1,
  },
  rootTeste: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  gridList: {
    width: '100%',
    height: can(SOBRAS_E_FALTAS) ? 254 : 234,
  },
  details: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  statsIcon: {
    color: '#557387',
  },
  statsIconAlert: {
    color: '#e14d2a',
    animation: '$blink .75s linear infinite',
  },
  actionsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.2rem',
    width: '100%',
    minHeight: 46,
    padding: '0 0.45rem',
    boxSizing: 'border-box',
  },
  statsItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '1 1 0',
    minWidth: 0,
    height: 46,
    lineHeight: 0,
  },
  compactIconButton: {
    width: 42,
    height: 42,
    padding: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  compactIcon: {
    fontSize: '1.72rem',
  },
  actionsWrapper: {
    width: '100%',
    minHeight: 46,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  },
}));

export default function Card({
  tank,
  alarms,
  maintenance,
  filter,
  onCallback,
  canOpenArqueacaoAutomaticaModal,
  openArqueacaoAutomaticaModal,
}) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [lang] = useState('pt-BR');
  const [em_manutencao] = React.useState(tank.em_manutencao);

  const showManualTanque = tank.tiposondatanquefisico != 0;

  const handleChangeTankMaintenance = useCallback(async (currentTank) => {
    try {
      await api.put('estoquestanquespostoscomboios', {
        idcliente: currentTank.graphic.idcliente,
        idposto: currentTank.graphic.idposto,
        idtanquefisico: currentTank.graphic.idtanquefisico,
        manutencaotanquefisico: em_manutencao ? 'N' : 'M',
      });
      toast.success(t('c.menu.stock.acaomanutencao.sucesso'));
      dispatch(filter({ page: Number(1), pagesize: 6 }));
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    }
  }, []);

  const colorAlarm = useMemo(
    () => (tank.alarmes ? classes.statsIconAlert : classes.statsIcon),
    [tank, classes]
  );

  const colorCommunication = useMemo(
    () =>
      tank.status_comunicacao ? classes.statsIconAlert : classes.statsIcon,
    [tank, classes]
  );

  const colorMaintenance = useMemo(
    () => (em_manutencao ? classes.statsIconAlert : classes.statsIcon),
    [em_manutencao, classes]
  );

  const colorResupply = useMemo(
    () =>
      tank.alerta_ressuprimento ? classes.statsIconAlert : classes.statsIcon,
    [tank, classes]
  );

  const colorDescarga = useMemo(
    () => (tank.em_descarga ? classes.statsIconAlert : classes.statsIcon),
    [tank, classes]
  );

  const colorArqueacao = useMemo(
    () =>
      tank.idhistoricoarqaut > 0 ? classes.statsIconAlert : classes.statsIcon,
    [tank, classes]
  );

  const occupiedPercent = useMemo(() => {
    if (!tank.tafi_capacidade) return 0;

    return Math.max(
      0,
      Math.min(100, (tank.tafi_volume_cont * 100) / tank.tafi_capacidade)
    );
  }, [tank]);

  const accountingPercent = useMemo(() => {
    if (!tank.tafi_capacidade || !can(SOBRAS_E_FALTAS)) return 0;

    return Math.max(
      0,
      Math.min(
        100,
        (tank.volcontabiltanquefisico * 100) / tank.tafi_capacidade
      )
    );
  }, [tank]);

  const chartBars = useMemo(() => {
    const bars = [];

    if (showManualTanque) {
      bars.push({
        key: 'fisico',
        header: 'F',
        headerColor: '#245f94',
        value: tank.fisico.value,
        percent: occupiedPercent,
        color: tank.fisico.color,
        opacity: true,
      });
    }

    if (can(SOBRAS_E_FALTAS)) {
      bars.push({
        key: 'contabil',
        header: 'C',
        headerColor: '#dc004e',
        value: tank.volcontabiltanquefisico,
        percent: accountingPercent,
        color: tank.fisico.color,
        opacity: false,
      });
    }

    if (!bars.length) {
      bars.push({
        key: 'fisico-fallback',
        header: 'F',
        headerColor: '#245f94',
        value: tank.fisico.value,
        percent: occupiedPercent,
        color: tank.fisico.color,
        opacity: true,
      });
    }

    return bars;
  }, [showManualTanque, tank, occupiedPercent, accountingPercent]);

  const rows = [
    {
      label: t('c.menu.stock.tanque'),
      value: tank.tafi_codigo,
    },
    {
      label: t('c.menu.stock.produto'),
      value: tank.prod_display,
    },
    {
      label: t('c.menu.stock.capacidade'),
      value: tank.tafi_capacidade?.toLocaleString(lang, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }),
    },
    {
      label: 'Capacidade Segurança',
      value: tank.capacidadeoperacionaltanquefisico?.toLocaleString(lang, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }),
    },
    {
      label: (
        <HighlightValue>{t('c.menu.stock.espacoLivre')}</HighlightValue>
      ),
      value: (
        <HighlightValue>
          {tank.tafi_live?.toLocaleString(lang, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })}
        </HighlightValue>
      ),
    },
    {
      label: t('c.menu.stock.ressuprimento'),
      value: tank.ressuprimento?.toLocaleString(lang, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }),
    },
  ];

  if (showManualTanque) {
    rows.push({
      label: t('c.menu.stock.estoqueFisico'),
      value: tank.tafi_volume_cont?.toLocaleString(lang, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }),
    });
  }

  if (can(SOBRAS_E_FALTAS)) {
    rows.push({
      label: t('c.menu.stock.estoquecontabil'),
      value: tank.volcontabiltanquefisico?.toLocaleString(lang, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }),
    });
  }

  if (can(SOBRAS_E_FALTAS) && showManualTanque) {
    rows.push({
      label: t('s.operacao.estoque.diferencafisicocontabil'),
      value: parseFloat(
        tank.tafi_volume_cont - tank.volcontabiltanquefisico
      ).toLocaleString(lang, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }),
    });
  }

  if (showManualTanque) {
    rows.push(
      {
        label: t('s.relatorio.descarganf.altura'),
        value: tank.alturanivel1tanquefisico?.toLocaleString(lang, {
          maximumFractionDigits: 1,
          minimumFractionDigits: 1,
        }),
      },
      {
        label: t('s.operacao.estoque.temperatura'),
        value: number(tank.temperaturamediatanquefisico, 1),
      },
      {
        label: t('s.geral.alturaagua'),
        value: number(tank.alturanivel2tanquefisico, 1),
      },
      {
        label: t('s.geral.volumeagua'),
        value: number(tank.volume2tanquefisico, 2),
      }
    );
  }

  rows.push({
    label: t(
      !showManualTanque
        ? 's.operacao.estoqueproduto.dataalteracao'
        : 'c.menu.stock.ultimacomunicacao'
    ),
    value: tank.comunicacao,
  });

  return (
    <Grid item xs={12} sm={12} md={4}>
      <CardMaterialUI
        style={{
          height: 404,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            padding: '8px 12px 6px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            align="center"
            style={{ paddingTop: 0, marginTop: -6, marginBottom: 4 }}
          >
            {tank.nome_posto}
          </Typography>

          <div className={classes.rootTeste}>
            <GridList
              cellHeight="100%"
              className={classes.gridList}
              cols={5}
              style={{ height: '100%', paddingBottom: 0 }}
            >
              <GridListTile key={1} cols={1}>
                <WrapperCharts charts={chartBars.length}>
                  {chartBars.map((bar) => (
                    <ChartCard key={bar.key}>
                      <ChartHeader color={bar.headerColor}>{bar.header}</ChartHeader>
                      <Chart
                        color={bar.color}
                        max={tank.fisico.max}
                        value={bar.value}
                        opacity={bar.opacity}
                      >
                        <span />
                      </Chart>
                      <ChartFooter>
                        <ChartValue>{`${bar.percent.toFixed(0)}%`}</ChartValue>
                      </ChartFooter>
                    </ChartCard>
                  ))}
                </WrapperCharts>
              </GridListTile>

              <WrapperGrid key={2} cols={4} active={can(SOBRAS_E_FALTAS)}>
                <div className={classes.details}>
                  <List className={classes.estoque} dense>
                    {rows.map((row) => (
                      <ListItem
                        key={row.label}
                        divider
                        className={classes.estoqueItem}
                      >
                        <ListItemText primary={row.label} />
                        <ListItemText
                          align="right"
                          secondaryTypographyProps={{ component: 'div' }}
                          secondary={row.value}
                        />
                      </ListItem>
                    ))}
                  </List>
                </div>
              </WrapperGrid>
            </GridList>
          </div>
        </CardContent>

        <Divider />

        <CardActions className={classes.actionsWrapper}>
          <div className={classes.actionsBar}>
            {showManualTanque && (
              <div className={classes.statsItem}>
                <IconButton
                  className={classes.compactIconButton}
                  disabled={!tank.alarmes}
                  onClick={() => alarms(tank)}
                >
                  <Tooltip title={t('c.menu.alarme.titulo')}>
                    <Notifications
                      className={`${classes.compactIcon} ${colorAlarm}`}
                    />
                  </Tooltip>
                </IconButton>
              </div>
            )}
            {showManualTanque && (
              <div className={classes.statsItem}>
                <Tooltip title={t('c.menu.stock.statuscomunicacao')}>
                  <span>
                    <IconButton className={classes.compactIconButton} disabled>
                      <Wifi
                        className={`${classes.compactIcon} ${colorCommunication}`}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            )}

            <div className={classes.statsItem}>
              <Tooltip title={t('c.menu.stock.alartaderesuprimento')}>
                <span>
                  <IconButton className={classes.compactIconButton} disabled>
                    <ChangeHistory
                      className={`${classes.compactIcon} ${colorResupply}`}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </div>

            {showManualTanque && (
              <div className={classes.statsItem}>
                <IconButton
                  className={classes.compactIconButton}
                  onClick={() => maintenance(tank)}
                >
                  <Tooltip title={t('c.menu.stock.acaomanutencao')}>
                    <BuildIcon
                      className={`${classes.compactIcon} ${colorMaintenance}`}
                    />
                  </Tooltip>
                </IconButton>
              </div>
            )}

            {showManualTanque && (
              <div className={classes.statsItem}>
                <IconButton
                  className={classes.compactIconButton}
                  onClick={() => onCallback(tank)}
                >
                  <Tooltip title={t('c.menu.stock.exibirtanquesdoposto')}>
                    <Timeline className={classes.compactIcon} />
                  </Tooltip>
                </IconButton>
              </div>
            )}

            {can(DESCARGA_NF) && tank.tipodescargaposto === 1 && (
              <div className={classes.statsItem}>
                <Tooltip title={t('c.menu.stock.acaorecementoemandamento')}>
                  <span>
                    <IconButton className={classes.compactIconButton} disabled>
                      <IconBarrel
                        className={`${classes.compactIcon} ${colorDescarga}`}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            )}

            {showManualTanque && can(MODULO_ARQUEACAO_AUTOMATICA) && (
              <div className={classes.statsItem}>
                <Tooltip
                  title={
                    tank.idhistoricoarqaut > 0
                      ? t('c.menu.stock.alertaarqueacaoautomatica')
                      : ''
                  }
                >
                  <span>
                    <IconButton
                      className={classes.compactIconButton}
                      disabled={
                        tank.idhistoricoarqaut <= 0 ||
                        !canOpenArqueacaoAutomaticaModal
                      }
                      onClick={() => openArqueacaoAutomaticaModal(tank)}
                    >
                      <Straighten
                        className={`${classes.compactIcon} ${colorArqueacao}`}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
        </CardActions>
      </CardMaterialUI>
    </Grid>
  );
}
